/*
This is pretty much a copy of:
react-scroll-to-component
https://github.com/flyingant/react-scroll-to-component
scroll-to
https://github.com/component/scroll-to
but switches to use a different Tween library
and fixes the SSR rendering
*/
import { env } from '@fiverr-private/futile';
import type { ScrollOptions, ScrollPosition, ScrollToComponentOptions } from './scrollToComponent.types';

const TWEEN = env.browser ? require('@tweenjs/tween.js') : () => undefined;

const scrollTo = (x: number, y: number, options: ScrollOptions = {}) => {
    // animate
    const animate = (time: number) => {
        requestAnimationFrame(animate);
        TWEEN.update(time);
    };

    requestAnimationFrame(animate);

    // start position
    const coords = scrollPos();

    // setup tween
    return new TWEEN.Tween(coords)
        .to({ x, y }, options.duration || 1000)
        .easing(TWEEN.Easing.Circular.Out)
        .onUpdate(() => {
            window.scrollTo(coords.x | 0, coords.y | 0);
        })
        .start();
};

const scrollPos = (): ScrollPosition => {
    const y = window.pageYOffset || document.documentElement.scrollTop;
    const x = window.pageXOffset || document.documentElement.scrollLeft;
    return { x, y };
};

const calculateScrollOffset = (ele: Element, offset = 0, alignment = 'middle'): number => {
    const body = document.body;
    const html = document.documentElement;

    const eleRect = ele.getBoundingClientRect();
    const clientHeight = html.clientHeight;
    const documentHeight = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
    );

    let scrollPosition;
    switch (alignment) {
        case 'top':
            scrollPosition = eleRect.top;
            break;
        case 'middle':
            scrollPosition = eleRect.bottom - clientHeight / 2 - eleRect.height / 2;
            break;
        case 'bottom':
            scrollPosition = eleRect.bottom - clientHeight;
            break;
        default:
            scrollPosition = eleRect.bottom - clientHeight / 2 - eleRect.height / 2;
            break; // default to middle
    }

    const maxScrollPosition = documentHeight - clientHeight;

    return Math.min(scrollPosition + offset + window.pageYOffset, maxScrollPosition);
};

/**
 * Scrolls to a component with animation
 * @param {Element} element - The element to scroll to
 * @param {ScrollToComponentOptions} options - Scroll options
 * @returns {object} A tween.js object
 */
export const scrollToComponent = (element: Element, options: ScrollToComponentOptions = {}) => {
    const { offset = 0, align = 'middle', duration } = options;

    return scrollTo(0, calculateScrollOffset(element, offset, align), { duration });
};
